import { useQuizContext } from "./QuizContext";
import { useState } from "react";
import QuizProduct from "./QuizProduct";
import { Markdown } from "@/snippets/globals/layout/Markdown";
import Styles from './Quiz.module.css';
import { Info } from "@/snippets/icons/Info";
import { Close } from "@/snippets/icons/Close";
import { useWindowDimensions } from "@/utils/Helper";
import { useCartAddItems, useProductsFromHandles } from "@backpackjs/storefront";
import Link from "next/link";
import { Image } from "@/snippets/components/Image";


export function QuizResult () {
  const context = useQuizContext();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const { vpWidth } = useWindowDimensions();
  const isMobile = vpWidth < 767;
  const {cartAddItems} = useCartAddItems();

  const {
    quizSettings,
    quizData,
    updateQuizData,
  } = context;
  const getResultsProducts = () => {
    const products = [];
    quizData.answers.forEach((answer) => {
      if(!answer) {return};
      const question = quizSettings.questions.find((question) => {
        return question.index === answer.questionIndex;
      })

      // Multiple answers question
      if(Array.isArray(answer.answer)) {
        let resultProductList = []
        const selectedOptions = question.options.filter((option, index) => {
          return answer.answer.includes(String(index));
        })

        selectedOptions.forEach((option) => {
          option.productList?.forEach((productObject) => {
            if(!productObject.product) {return;}

            resultProductList.push({
              product: productObject.product,
              routine: productObject.routineType,
              resultMessage: productObject.resultMessage,
            })
          })
        })

        products = [...products, ...resultProductList];
      } else {
        // Single answer question
        if(answer.answer.indexOf('-') > -1) {
          // Question with follow up question
          const mainOption = question.options[Number.parseInt(answer.answer.split('-')[0])];
          const followUpOption = question.options[Number.parseInt(answer.answer.split('-')[0])].followUpOptions[Number.parseInt(answer.answer.split('-')[1])];
          const mainOptionProducts = mainOption?.productList?.map((productObject) => {
            if(!!productObject.product) {
              return {
                product: productObject.product,
                routine: productObject.routineType,
                resultMessage: productObject.resultMessage,
              }
            }
          })
          const followUpOptionProducts = followUpOption?.productList?.map((productObject) => {
            if(!!productObject.product) {
              return {
                product: productObject.product,
                routine: productObject.routineType,
                resultMessage: productObject.resultMessage,
              }
            }
          })
          if(mainOptionProducts) {
            products = [...products, ...mainOptionProducts]
          }
          if(followUpOptionProducts) {
            products = [...products, ...followUpOptionProducts]
          }
        } else {
          // Question without follow up question
          const option = question.options.find((option, index) => {
            return String(index) === answer.answer;
          })

          const optionProducts = option?.productList?.map((productObject) => {
            if(!!productObject.product) {
              return {
                product: productObject.product,
                routine: productObject.routineType,
                resultMessage: productObject.resultMessage,
              }
            }
          })

          if(option && optionProducts) {
            products = [...products, ...optionProducts]
          }
        }
      }
    })

    return products;
  }

  const buildSummaryResults = () => {
    const resultsArray = [];
    quizData.answers.forEach((answerItem) => {
      if(!answerItem) {return;}
      const currentQuestion = quizData.filteredQuestions.find((question) => question.index === answerItem.questionIndex);

      if(currentQuestion.isFilterQuestion == true) {
        return;
      }

      let answer = null;

      if (answerItem.answer.indexOf('-') > -1) {
        const mainQuestionAnswer = currentQuestion.options[answerItem.answer.split('-')[0]].label;
        const followUpQuestionAnswer = currentQuestion.options[answerItem.answer.split('-')[0]].followUpOptions[answerItem.answer.split('-')[1]].label;
        answer = mainQuestionAnswer + ' ' + followUpQuestionAnswer;
      } else {
        answer = Array.isArray(answerItem.answer) ?
        answerItem.answer.map((index) => currentQuestion.options?.[index]?.label).join(', ') : currentQuestion.options[answerItem.answer]?.label;
      }

      resultsArray.push({
        label: currentQuestion.summaryLabel,
        answer,
      })
    })

    return resultsArray.map((result, index) => {
      return (
        <li key={`result-${index}`} className="leading-relaxed m-0">
          <span>{result.label}:</span>
          <span className="font-gothamBold ml-1">{result.answer}</span>
        </li>
      )

    })
  }

  const toggleResultSummary = () => {
    setSummaryOpen(!summaryOpen);
  }

  const addAllProductsToCart = async () => {
    const itemsToAdd = parsedProducts.map((product) => {
      return {
        merchandiseId: product.variants[0].id,
        quantity: 1,
      }
    })

    cartAddItems(itemsToAdd).then(() => {
      window.dispatchEvent(new CustomEvent('openSideCart'));
    });
  }

  const resultProducts = getResultsProducts();
  const {products: parsedProducts} = useProductsFromHandles({handles: resultProducts.map((productObject) => productObject?.product?.handle)});
  const dailyProducts = resultProducts.filter((productObject) => productObject?.routine == 'daily');
  const specializedProducts = resultProducts.filter((productObject) => productObject?.routine == 'specialized');

  const summaryPopup = (
    <div className="quiz-result__summary-content absolute left-1/2 -mt-[1.5em] w-[348px] max-w-full -translate-x-1/2 translate-y-[2em] border border-grey-9 bg-white py-4 px-5 text-left lg:top-0 lg:mt-0 md:max-w-none">
      <span className="absolute top-[-1px] left-1/2 hidden h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-t border-l border-grey-9 bg-white md:block"></span>
      <span className="block pr-5 font-gothamBold text-xs leading-normal">
        Your quiz selections:
      </span>
      <button
        type="button"
        onClick={toggleResultSummary}
        className="absolute top-5 right-4"
      >
        <div className="sr-only">Close summary</div>
        <Close className="h-2.5 w-2.5" />
      </button>
      <ol className="my-2 list-inside pl-0 text-xs">{buildSummaryResults()}</ol>
    </div>
  );

  return (
    <div>
      <div className="quiz-result__header mx-auto w-[720px] max-w-full px-5 pt-[78px] pb-[60px] text-center">
        <h1 className="h4 mb-5 font-tiempos"> {quizSettings.resultTitle} </h1>
        <div>
          {quizSettings.resultDescription}
          <span className="quiz-result__info-container relative inline-block">
            <button
              type="button"
              onClick={toggleResultSummary}
              className="ml-1 align-middle"
            >
              <div className="sr-only">Open summary</div>
              <Info className="h-5 w-5" />
              {summaryOpen && isMobile && (
                <span className="absolute left-1/2 -bottom-1/2 z-20 mb-[-1px] h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-t border-l border-grey-9 bg-white"></span>
              )}
            </button>
            {summaryOpen && !isMobile && summaryPopup}
          </span>
          {summaryOpen && isMobile && summaryPopup}
        </div>
      </div>
      <div className="quiz-result__body-content bg-grey-9 py-12">
        {dailyProducts.length > 0 && (
          <div className={`quiz-result__daily-routine-group lg-px-0 mx-auto w-[720px] max-w-full border-[#979797] px-5 ${dailyProducts.length && specializedProducts.length && 'lg:border-b'} `}>
            <div className="quiz-result__daily-routine-header text-center">
              <h2 className="quiz-result__routine-title h3 mb-2">
                {quizSettings.baseRoutineTitle}
              </h2>
              <Markdown content={quizSettings.baseRoutineDescription} />
            </div>
            <div
              className={`quiz-result__product-grid flex flex-wrap lg:px-0 ${
                dailyProducts.length % 2 !== 0 &&
                Styles['quiz-result__product-grid--odd']
              }`}
            >
              {dailyProducts.map((productData, index) => (
                <div className="grid-item w-full border-t border-[#979797] pt-7 first:border-t-0 lg:w-6/12 lg:border-t-0 lg:border-none lg:pt-0 lg:odd:pr-10 lg:even:pl-10 mb-6">
                  <QuizProduct
                    productData={productData}
                    index={index}
                    key={productData.product.id}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {specializedProducts.length > 0 && (
          <div className="quiz-result__specialized-routine-group lg-px-0 mx-auto mt-12 w-[720px] max-w-full px-5">
            <div className="quiz-result__daily-routine-header text-center">
              <h2 className="quiz-result__routine-title h3 mb-2">
                {quizSettings.specializedRoutineTitle}
              </h2>
              <Markdown content={quizSettings.specializedRoutineDescription} />
            </div>
            <div
              className={`quiz-result__product-grid flex flex-wrap lg:px-0 ${
                specializedProducts.length % 2 !== 0 &&
                Styles['quiz-result__product-grid--odd']
              }`}
            >
              {specializedProducts.map((productData, index) => (
                <div className="grid-item w-full border-t border-[#979797] pt-7 first:border-t-0 lg:w-6/12 lg:border-t-0 lg:border-none lg:pt-0 lg:odd:pr-10 lg:even:pl-10 mb-6">
                  <QuizProduct
                    productData={productData}
                    index={index}
                    key={productData.product.id}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="quiz-result__cta-container my-14 px-4 text-center">
        <h3 className="mx-auto max-w-lg">
          Want it all? Add your entire customized routine to cart!
        </h3>
        <div>
          <button
            type="button"
            className="quiz-result__add-all-products-btn button button--primary"
            onClick={addAllProductsToCart}
          >
            {quizSettings.addAllLabel}
          </button>
        </div>
        {quizSettings.enableSaveResults && (
          <>
            <span className="my-2 block">or</span>
            <div>
              <button
                type="button"
                className="quiz-result__save-results-btn button button--checkout"
              >
                {quizSettings.saveResultsLabel}
              </button>
            </div>
          </>
        )}
        <div className="mt-4">
          <button
            type="button"
            className="focus-link"
            onClick={() => {
              updateQuizData({
                quizStep: 'steps',
                currentQuestion: 0,
                answers: [],
                isFiltered: false,
                hasError: false,
              });
            }}
          >
            retake the quiz
          </button>
        </div>
      </div>
      <div className="quiz-result__banner container my-14 mx-auto lg:px-4 relative">
        <div className="quiz-result__banner-image">
        {isMobile ? (
            <Image
              src={quizSettings.bannerImageMobile?.src}
              alt={quizSettings.bannerImageMobile?.alt}
              width={quizSettings.bannerImageMobile?.width}
              height={quizSettings.bannerImageMobile?.height}
              className="w-full"
            />
          ) : (
            <Image
              src={quizSettings.bannerImageDesktop?.src}
              alt={quizSettings.bannerImageDesktop?.alt}
              width={quizSettings.bannerImageDesktop?.width}
              height={quizSettings.bannerImageDesktop?.height}
              className="w-full"
            />
          )}
        </div>
        <div
          className={`quiz-result__banner-info lg:absolute top-1/2 lg:-translate-y-1/2 px-5 lg:px-0 lg:ml-20 lg:max-w-[344px] text-center lg:text-left mt-8 lg:mt-0 ${
            quizSettings.desktopBannerLightText ? 'lg:text-white' : 'text-black'
          }`}
        >
          {quizSettings.resultBannerTitle && (
            <h3 className="text-h3-m lg:text-h2 mb-0">{quizSettings.resultBannerTitle}</h3>
          )}
          {quizSettings.resultBannerBody && (
            <Markdown content={quizSettings.resultBannerBody} className="mt-5 lg:mt-8 last:mb-0"/>
          )}
          {quizSettings.resultBannerLink?.url &&
            quizSettings.resultBannerLink?.text && (
              <Link href={quizSettings.resultBannerLink?.url}>
                <a
                  className={`button button--secondary inline-block no-underline mt-5 lg:mt-8 ${
                    quizSettings.desktopBannerLightText &&
                    'lg:!border-white lg:!text-white lg:hover:!bg-white lg:hover:!text-black'
                  }`}
                  target={
                    quizSettings.resultBannerLink.newTab ? '_blank' : '_self'
                  }
                >
                  {quizSettings.resultBannerLink?.text}
                </a>
              </Link>
            )}
        </div>
      </div>
    </div>
  );
}
